import { isAxiosError } from 'axios';
import type { Location } from 'react-router-dom';
import { apiClient } from './api';
import { getTomRouteLabel } from './tomRouteContext';

export interface TomMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

export interface TomScreenPayload {
  type: string;
  data: Record<string, unknown>;
  timestamp: string;
  navigation: {
    pathname: string;
    search: string;
    routeLabel: string;
  };
}

export function buildTomScreenPayload(
  screenData: Record<string, unknown> | null | undefined,
  location: Pick<Location, 'pathname' | 'search'>
): TomScreenPayload {
  const navigation = {
    pathname: location.pathname,
    search: location.search || '',
    routeLabel: getTomRouteLabel(location.pathname),
  };
  if (screenData != null) {
    return { ...screenData, navigation } as TomScreenPayload;
  }
  return {
    type: 'generic',
    data: {},
    timestamp: new Date().toISOString(),
    navigation,
  };
}

export interface TomAssistantResult {
  reply: string;
  tokensUsed?: number;
}

export async function callTomAssistant(
  userMsg: string,
  screenPayload: TomScreenPayload,
  pageContext: string
): Promise<TomAssistantResult> {
  const aiResponse = await apiClient.post(
    '/assistant',
    {
      userMsg,
      screenData: screenPayload,
      pageContext,
    },
    { timeout: 30000 }
  );

  if (aiResponse.data?.success) {
    return {
      reply: aiResponse.data.answer,
      tokensUsed: aiResponse.data.tokens_used,
    };
  }

  if (aiResponse.status === 503) {
    const d = aiResponse.data as { error?: string; hint?: string; details?: string } | undefined;
    const parts = [d?.error, d?.hint].filter(Boolean) as string[];
    if (import.meta.env.DEV && d?.details) parts.push(String(d.details));
    return {
      reply:
        parts.join(' ') ||
        'Tom ist derzeit nicht verfügbar (LLM-Hub). Bitte Hub-Status und Modellrichtlinie prüfen.',
    };
  }

  return {
    reply: 'Entschuldigung, es gab ein Problem bei der Verarbeitung. Bitte versuchen Sie es erneut.',
  };
}

export function tomAssistantErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data) {
    const d = error.response.data as {
      details?: string;
      error?: string;
      upstreamStatus?: number;
    };
    if (d.upstreamStatus === 401 || String(d.details || '').includes('401')) {
      return 'Spracherkennung: WestAI hat den API-Key abgelehnt (401). Bitte WESTAI_API_KEY im Hub-Core prüfen.';
    }
    if (typeof d.details === 'string' && d.details.trim()) {
      return `Anfrage fehlgeschlagen: ${d.details.slice(0, 200)}`;
    }
  }
  return 'Verbindungsfehler. Bitte versuchen Sie es erneut.';
}
